import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Instagram, MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { ROSEBAR, NAV_LINKS } from "../lib/constants";
import { ReservationPolicyShort } from "./ReservationPolicy";
import { api } from "../lib/api";
import { toast } from "sonner";

// When the URL has `?embed=1` (or the page is loaded inside an <iframe> on a
// different origin), we hide the top nav + footer + chat bubble so the page
// renders cleanly as a widget inside platforms like Duda, Wix, Squarespace.
const useEmbedMode = () => {
  const [embed, setEmbed] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("embed") === "1") {
      setEmbed(true);
      document.body.classList.add("rosebar-embed");
    }
  }, []);
  return embed;
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between h-[72px] md:h-[78px]">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center shrink-0"
          onClick={() => {
            if (loc.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <img src={ROSEBAR.logo_white} alt="Rosebar DC" className="h-8 md:h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1) || "home"}`}
              className={({ isActive }) =>
                `px-3 py-1.5 text-[13px] font-medium rounded-full transition-colors ${
                  isActive ? "text-white bg-white/[0.06]" : "text-white/65 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <Link to="/account" data-testid="header-signin" className="px-3.5 py-1.5 text-[13px] font-medium text-white/70 hover:text-white rounded-full transition-colors">
            Sign in
          </Link>
          <Link to="/reservations" data-testid="header-reserve-btn" className="btn-primary !py-2 !px-4 !text-[13px]">
            Reserve <ArrowRight size={13} />
          </Link>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-dark border-t border-white/10" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.slice(1) || "home"}`}
                className="py-3 text-sm tracking-[0.2em] uppercase text-white/90 border-b border-white/5"
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/account" className="py-3 text-sm tracking-[0.2em] uppercase text-white/90 border-b border-white/5" data-testid="mobile-nav-account">
              Sign in
            </Link>
            <Link to="/reservations" className="btn-primary mt-5 w-full" data-testid="mobile-reserve-btn">
              Reserve VIP Table
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/newsletter", { email, source: "footer" });
      toast.success("On the list. Welcome to Rosebar.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="relative z-10 border-t border-white/5 bg-black mt-32" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Link to="/" data-testid="footer-logo-link" aria-label="Rosebar home" className="inline-block mb-6">
              <img src={ROSEBAR.logo_white} alt="Rosebar" className="h-10 w-auto" />
            </Link>
            <p className="font-display text-2xl md:text-3xl text-white/90 leading-tight max-w-md">
              Where culture, music & influence meet — in the heart of Dupont Circle.
            </p>
            <form onSubmit={subscribe} className="mt-8 max-w-md" data-testid="newsletter-form">
              <label className="label-eyebrow block mb-3">Join the Inner Circle</label>
              <div className="flex gap-0">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="!rounded-none"
                  data-testid="newsletter-email"
                />
                <button type="submit" disabled={submitting} className="btn-primary !rounded-none !px-6" data-testid="newsletter-submit">
                  {submitting ? "…" : "Join"}
                </button>
              </div>
              <p className="text-xs text-white/40 mt-3">Event drops · Celebrity nights · Member-only invites.</p>
            </form>
          </div>

          <div className="lg:col-span-3">
            <h4 className="label-eyebrow mb-5">Visit</h4>
            <a
              href={ROSEBAR.google_maps}
              target="_blank"
              rel="noreferrer"
              className="text-white/80 hover:text-[#E8B4B8] flex items-start gap-3 mb-3"
              data-testid="footer-address"
            >
              <MapPin size={16} className="mt-1 text-[#B76E79]" />
              <span className="text-sm leading-relaxed">{ROSEBAR.address}</span>
            </a>
            <a href={`tel:${ROSEBAR.phone_call}`} className="text-white/80 hover:text-[#E8B4B8] flex items-start gap-3 mb-3" data-testid="footer-call">
              <Phone size={16} className="mt-1 text-[#B76E79]" />
              <span className="text-sm">
                {ROSEBAR.phone_call}
                <span className="block text-xs text-white/40">{ROSEBAR.phone_call_label}</span>
              </span>
            </a>
            <a href={`sms:${ROSEBAR.phone_text}`} className="text-white/80 hover:text-[#E8B4B8] flex items-start gap-3 mb-3" data-testid="footer-text">
              <Phone size={16} className="mt-1 text-[#B76E79]" />
              <span className="text-sm">
                {ROSEBAR.phone_text}
                <span className="block text-xs text-white/40">{ROSEBAR.phone_text_label}</span>
              </span>
            </a>
            <a href={`mailto:${ROSEBAR.email}`} className="text-white/80 hover:text-[#E8B4B8] flex items-start gap-3" data-testid="footer-email">
              <Mail size={16} className="mt-1 text-[#B76E79]" />
              <span className="text-sm">{ROSEBAR.email}</span>
            </a>
          </div>

          <div className="lg:col-span-2">
            <h4 className="label-eyebrow mb-5">Hours</h4>
            <ul className="space-y-1.5">
              {ROSEBAR.hours.map((h) => (
                <li key={h.day} className="text-xs text-white/70 flex justify-between gap-4">
                  <span className="text-white/50">{h.day}</span>
                  <span className="text-right">{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="label-eyebrow mb-5">Explore</h4>
            <ul className="space-y-2">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/70 hover:text-[#E8B4B8]" data-testid={`footer-link-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/terms" className="text-sm text-white/70 hover:text-[#E8B4B8]" data-testid="footer-link-terms">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm text-white/70 hover:text-[#E8B4B8]" data-testid="footer-link-privacy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/admin/login" className="text-sm text-white/30 hover:text-white/60" data-testid="footer-admin">
                  Admin
                </Link>
              </li>
              <li>
                <Link to="/promoter/login" className="text-sm text-white/30 hover:text-white/60" data-testid="footer-promoter">
                  Promoter Portal
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16">
          <ReservationPolicyShort />
        </div>

        <div className="mt-8 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-xs text-white/40">© {new Date().getFullYear()} Rosebar Lounge · Washington, DC. All rights reserved.</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/30">
              Website by{" "}
              <a
                href="https://webnehtech.com"
                target="_blank"
                rel="noreferrer"
                className="text-[#E8B4B8] hover:text-[#D4AF7A] transition"
                data-testid="footer-credit"
              >
                WebNehTech
              </a>
            </p>
          </div>
          <div className="flex items-center gap-5">
            <a href={ROSEBAR.instagram} target="_blank" rel="noreferrer" className="text-white/50 hover:text-[#E8B4B8]" data-testid="social-instagram">
              <Instagram size={18} />
            </a>
            <a href={ROSEBAR.tiktok} target="_blank" rel="noreferrer" className="text-white/50 hover:text-[#E8B4B8] text-xs tracking-widest" data-testid="social-tiktok">
              TIKTOK
            </a>
            <a href={ROSEBAR.twitter} target="_blank" rel="noreferrer" className="text-white/50 hover:text-[#E8B4B8] text-xs tracking-widest" data-testid="social-twitter">
              X
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function Layout() {
  const embed = useEmbedMode();
  return (
    <div className="min-h-screen relative" data-testid="site-layout">
      {!embed && <Header />}
      <main className={`relative z-10 ${embed ? "pt-0" : ""}`}>
        <Outlet context={{ embed }} />
      </main>
      {!embed && <Footer />}
      {/* Chat widget removed — Hero "Live Chat" button opens a Text/Call popover instead */}
    </div>
  );
}
